import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Trash2, GripVertical, X, Loader2 } from 'lucide-react';
import { api } from '../../../lib/api';
import { useOrg } from '../../../hooks/useOrg';

const TYPE_OPTIONS = [
  { value: 'text',         label: 'Single line text' },
  { value: 'textarea',     label: 'Multi-line text' },
  { value: 'number',       label: 'Number' },
  { value: 'date',         label: 'Date' },
  { value: 'select',       label: 'Dropdown (single)' },
  { value: 'multi_select', label: 'Dropdown (multi)' },
  { value: 'boolean',      label: 'Checkbox (yes/no)' },
  { value: 'phone',        label: 'Phone' },
  { value: 'email',        label: 'Email' },
  { value: 'url',          label: 'URL' },
];

function FieldEditor({ field, onSave, onCancel, onDelete }) {
  const [draft, setDraft] = useState(field || {
    label: '', type: 'text', section: 'Custom', required: false, placeholder: '', help_text: '', options: [],
  });
  const [optionsText, setOptionsText] = useState(
    Array.isArray(draft.options) ? draft.options.map(o => o.label || o.value || o).join('\n') : ''
  );

  const needsOptions = draft.type === 'select' || draft.type === 'multi_select';

  const handleSave = () => {
    const out = { ...draft };
    if (needsOptions) {
      out.options = optionsText.split('\n').map(s => s.trim()).filter(Boolean)
        .map(label => ({ value: label.toLowerCase().replace(/[^a-z0-9]+/g, '_'), label }));
    } else {
      out.options = null;
    }
    onSave(out);
  };

  const update = (k, v) => setDraft(d => ({ ...d, [k]: v }));

  return (
    <div style={{
      background: 'var(--bg-elevated, #fff)',
      border: '1px solid rgba(11,11,20,0.08)',
      borderRadius: 14,
      padding: 18,
      marginBottom: 12,
    }}>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.4fr 1fr', gap: 12, marginBottom: 12 }}>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'rgba(11,11,20,0.55)', display: 'block', marginBottom: 4 }}>Label</label>
          <input className="op-input" value={draft.label} onChange={e => update('label', e.target.value)} placeholder="e.g. Defense counsel" />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'rgba(11,11,20,0.55)', display: 'block', marginBottom: 4 }}>Type</label>
          <select className="op-select" value={draft.type} onChange={e => update('type', e.target.value)}>
            {TYPE_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'rgba(11,11,20,0.55)', display: 'block', marginBottom: 4 }}>Section</label>
          <input className="op-input" value={draft.section} onChange={e => update('section', e.target.value)} placeholder="Custom" />
        </div>
      </div>

      {needsOptions && (
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'rgba(11,11,20,0.55)', display: 'block', marginBottom: 4 }}>
            Options (one per line)
          </label>
          <textarea className="op-input" rows={4} value={optionsText} onChange={e => setOptionsText(e.target.value)}
            placeholder={'Option 1\nOption 2\nOption 3'} style={{ fontFamily: 'inherit', resize: 'vertical' }} />
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'rgba(11,11,20,0.55)', display: 'block', marginBottom: 4 }}>Placeholder</label>
          <input className="op-input" value={draft.placeholder || ''} onChange={e => update('placeholder', e.target.value)} />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'rgba(11,11,20,0.55)', display: 'block', marginBottom: 4 }}>Help text</label>
          <input className="op-input" value={draft.help_text || ''} onChange={e => update('help_text', e.target.value)} />
        </div>
      </div>

      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, marginRight: 16 }}>
        <input type="checkbox" checked={!!draft.required} onChange={e => update('required', e.target.checked)} />
        Required
      </label>

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button className="op-btn op-btn-primary" onClick={handleSave} disabled={!draft.label || !draft.type}>Save field</button>
        <button className="op-btn op-btn-secondary" onClick={onCancel}>Cancel</button>
        {field?.id && (
          <button className="op-btn op-btn-danger" onClick={onDelete} style={{ marginLeft: 'auto' }}>
            <Trash2 size={12} /> Delete
          </button>
        )}
      </div>
    </div>
  );
}

export default function CustomFieldsTab() {
  const { currentOrg } = useOrg();
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // field object (existing) or 'new'

  const refresh = useCallback(() => {
    if (!currentOrg) return;
    setLoading(true);
    api.listCustomFields(currentOrg.id)
      .then(setFields).catch(e => setError(e.message)).finally(() => setLoading(false));
  }, [currentOrg]);

  useEffect(() => { refresh(); }, [refresh]);

  const active = fields.filter(f => !f.archived_at);
  const archived = fields.filter(f => f.archived_at);

  const handleSave = async (draft) => {
    setError(null);
    try {
      if (draft.id) {
        await api.updateCustomField(currentOrg.id, draft.id, draft);
      } else {
        await api.createCustomField(currentOrg.id, draft);
      }
      setEditing(null);
      refresh();
    } catch (e) { setError(e.message); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Archive this field? Existing values stay in the database but the field stops appearing on profiles.')) return;
    try {
      await api.deleteCustomField(currentOrg.id, id);
      setEditing(null);
      refresh();
    } catch (e) { setError(e.message); }
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Client custom fields</h2>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: 'rgba(11,11,20,0.55)' }}>
            Define your own fields that appear on every client profile. Useful for case-specific data the built-in PI fields don't cover.
          </p>
        </div>
        {!editing && (
          <button className="op-btn op-btn-primary" onClick={() => setEditing('new')}>
            <Plus size={12} /> Add field
          </button>
        )}
      </div>

      {error && <div className="op-banner danger" style={{ marginTop: 12 }}>{error}</div>}

      {editing && (
        <div style={{ marginTop: 16 }}>
          <FieldEditor
            field={editing === 'new' ? null : editing}
            onSave={handleSave}
            onCancel={() => setEditing(null)}
            onDelete={() => handleDelete(editing.id)}
          />
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 32, color: 'rgba(11,11,20,0.55)', fontSize: 13 }}>
          <Loader2 size={16} className="animate-spin" style={{ display: 'inline', marginRight: 8 }} /> Loading…
        </div>
      ) : (
        <div style={{ marginTop: 16 }}>
          {active.length === 0 && !editing && (
            <div style={{ textAlign: 'center', padding: 48, color: 'rgba(11,11,20,0.55)', fontSize: 13 }}>
              No custom fields yet. Click "Add field" to create your first one.
            </div>
          )}
          {active.map(f => (
            <div key={f.id} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '14px 16px', borderRadius: 10,
              border: '1px solid rgba(11,11,20,0.08)', marginBottom: 8,
              background: 'white', cursor: 'pointer',
            }} onClick={() => setEditing(f)}>
              <GripVertical size={14} style={{ color: 'rgba(11,11,20,0.3)' }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>
                  {f.label}
                  {f.required && <span style={{ color: '#dc2626', marginLeft: 4 }}>*</span>}
                </div>
                <div style={{ fontSize: 11, color: 'rgba(11,11,20,0.55)', marginTop: 2 }}>
                  {f.type}{f.section && f.section !== 'Custom' ? ` · ${f.section}` : ''}
                </div>
              </div>
              <button onClick={(e) => { e.stopPropagation(); handleDelete(f.id); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(11,11,20,0.4)', padding: 4 }}>
                <X size={14} />
              </button>
            </div>
          ))}
          {archived.length > 0 && (
            <details style={{ marginTop: 16 }}>
              <summary style={{ fontSize: 12, color: 'rgba(11,11,20,0.55)', cursor: 'pointer' }}>
                {archived.length} archived field{archived.length === 1 ? '' : 's'}
              </summary>
              <div style={{ marginTop: 8 }}>
                {archived.map(f => (
                  <div key={f.id} style={{ padding: '10px 14px', fontSize: 12, color: 'rgba(11,11,20,0.55)', borderRadius: 8, background: 'rgba(11,11,20,0.04)', marginBottom: 4 }}>
                    {f.label} ({f.type})
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
